import { useEffect, useState } from 'react';
import { Button, Col, Divider, Flex, Form, Input, Modal, Row } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// services
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';

// components
import {
	onApproveCommercialEntity,
	onDeleteCommercialEntity,
	onGetCommercialEntityById,
	onRejectCommercialEntity,
	onUnlinkCommercialEntity,
	onUpdateCommercialEntity,
} from 'modules/commercialEntities/services/commercialEntities';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import AuthActionComponent from 'components/authAction/authAction';
import FilePreview from 'components/filePreview/filePreview';
import BackButton from 'components/backButton/backButton';
import Icon from 'utils/icon';

// models
import { CommercialEntity } from 'modules/commercialEntities/models/commercialEntities';

//styles
import styles from './view.module.scss';
import PDFViewer from 'components/pdfViewer/pdfViewer';
import { StatusColors } from 'utils/statusColors';
import { MapResponseErrors } from 'utils/mapResponseErrors';
import { formatErrorMessages } from 'utils/errorMessage';

function CommercialEntityViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [updateForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState(true);
	const [commercialEntityDetails, setCommercialEntityDetails] = useState<CommercialEntity>();

	// actions states
	const [isUpdating, setIsUpdating] = useState(false);
	const [isUnlinking, setIsUnlinking] = useState(false);
	const [isApproving, setIsApproving] = useState(false);
	const [isRejecting, setIsRejecting] = useState(false);

	const [showEditModal, setShowEditModal] = useState(false);
	const [showUnlinkingConfirmation, setShowUnlinkingConfirmation] = useState(false);
	const [showApprovingConfirmation, setShowApprovingConfirmation] = useState(false);
	const [showRejectingConfirmation, setShowRejectingConfirmation] = useState(false);

	useEffect(() => {
		getCommercialEntity();
	}, []);

	const getCommercialEntity = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCommercialEntityById(id as string);

			setCommercialEntityDetails(response.data.data);

			updateForm.setFieldsValue({
				'[name][ar]': response.data.data.investment_entity_name?.ar,
				'[name][en]': response.data.data.investment_entity_name?.en,
				cr_number: response.data.data.investment_entity_cr_number,
				unified_cr: response.data.data.investmentEntity.unified_cr,
			});

			setIsLoading(false);
		} catch (error: any) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			navigate('/commercial-entities');
		}
	};

	const onUpdate = async () => {
		try {
			setIsUpdating(true);
			await onUpdateCommercialEntity(id!, {
				name: {
					ar: updateForm.getFieldValue('[name][ar]'),
					en: updateForm.getFieldValue('[name][en]'),
				},
				cr_number: updateForm.getFieldValue('cr_number'),
				unified_cr: updateForm.getFieldValue('unified_cr'),
			});

			getCommercialEntity();

			NotificationService.success(t('COMMON.SUCCESS'), t('COMMERCIAL_ENTITIES.UPDATED_SUCCESS'));
			setShowEditModal(false);
			setIsUpdating(false);
		} catch (error) {
			setIsUpdating(false);

			MapResponseErrors(error, updateForm);

			NotificationService.error(t('COMMON.OPS'), t('COMMERCIAL_ENTITIES.UPDATE_ERROR'));
		}
	};

	const onApprove = async () => {
		try {
			setIsApproving(true);
			await onApproveCommercialEntity(id!);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY_APPROVED'));
			setShowApprovingConfirmation(false);
			setIsApproving(false);
			getCommercialEntity();
		} catch (error) {
			setIsApproving(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY_NOT_APPROVED'));
		}
	};

	const onUnlink = async () => {
		try {
			setIsUnlinking(true);
			await onUnlinkCommercialEntity(id!);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY_UNLINKED'));
			setShowUnlinkingConfirmation(false);
			setIsUnlinking(false);
			getCommercialEntity();
		} catch (error) {
			setIsUnlinking(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY_NOT_UNLINKED'));
		}
	};

	const onReject = async (message: string) => {
		try {
			setIsRejecting(true);
			await onRejectCommercialEntity(id!, message);
			NotificationService.success(t('COMMON.SUCCESS'), t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY_REJECTED'));
			setShowRejectingConfirmation(false);
			setIsRejecting(false);
			getCommercialEntity();
		} catch (error) {
			setIsRejecting(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY_NOT_REJECTED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
			</div>

			<h3 className="fw-semibold mb-4">{t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY_DETAILS')}</h3>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer`}>
					<div>
						<div className="d-flex align-items-center justify-content-between mb-4">
							<h5 className="fw-semibold text-primary mb-0">{t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY_DETAILS')}</h5>
							<div className="d-flex gap-2">
								<AuthActionComponent action="investment_entity_update">
									<Button
										color="primary"
										variant="filled"
										onClick={() => {
											setShowEditModal(true);
										}}>
										{t('COMMON.EDIT')}
									</Button>

									{commercialEntityDetails?.status_key == 'Pending' && (
										<>
											<Button
												color="primary"
												variant="filled"
												onClick={() => {
													setShowApprovingConfirmation(true);
												}}>
												{t('COMMERCIAL_ENTITIES.APPROVE')}
											</Button>
											<Button
												color="danger"
												variant="filled"
												onClick={() => {
													setShowRejectingConfirmation(true);
												}}>
												{t('COMMERCIAL_ENTITIES.REJECT')}
											</Button>
										</>
									)}

									{commercialEntityDetails?.status_key == 'Approved' && (
										<Button
											type="link"
											color="danger"
											variant="filled"
											onClick={() => {
												setShowUnlinkingConfirmation(true);
											}}>
											{t('COMMERCIAL_ENTITIES.UNLINK')}
										</Button>
									)}
								</AuthActionComponent>
							</div>
						</div>

						<div className={styles.data}>
							<div className={styles.data_logo} style={{ alignItems: isRTL() ? 'start' : 'center' }}>
								{commercialEntityDetails?.investmentEntity?.logo_preview ? (
									<img src={commercialEntityDetails?.investmentEntity?.logo_preview} />
								) : isRTL() ? (
									commercialEntityDetails?.investment_entity_name?.ar?.slice(0, 1)
								) : (
									commercialEntityDetails?.investment_entity_name?.en?.slice(0, 1)
								)}
							</div>
							<div>
								<div className="w-100">
									<div className={styles.data_name}>
										<h5>
											{isRTL() ? commercialEntityDetails?.investment_entity_name?.ar : commercialEntityDetails?.investment_entity_name?.en}
										</h5>

										<span className={`status ${StatusColors[commercialEntityDetails?.investmentEntity?.profile_status!]}`}>
											{t(`COMMON.${commercialEntityDetails?.investmentEntity?.profile_status?.toUpperCase()}`)}
										</span>
									</div>

									<div className={styles.data_connection}>
										<Row gutter={[10, 10]} className="w-100">
											<Col span={24}>
												<Flex gap={2} align="center">
													<span>{t('COMMERCIAL_ENTITIES.UNIFIED_CR')}</span> :
													<span>{commercialEntityDetails?.investmentEntity?.unified_cr}</span>
												</Flex>
											</Col>
											<Col span={24}>
												<Flex gap={2} align="center">
													<span>{t('COMMERCIAL_ENTITIES.CR_NUMBER')}</span> :
													<span>{commercialEntityDetails?.investment_entity_cr_number}</span>
												</Flex>
											</Col>
										</Row>
									</div>
								</div>
							</div>
						</div>
					</div>

					<Divider />

					<div>
						<h5 className="fw-semibold mb-4 text-primary">{t('COMMERCIAL_ENTITIES.GARDIAN_DETAILS')}</h5>
						<div className={styles.data}>
							<div className={styles.data_logo} style={{ alignItems: isRTL() ? 'start' : 'center' }}>
								{commercialEntityDetails?.guardianUser?.avatar_preview ? (
									<img src={commercialEntityDetails?.guardianUser?.avatar_preview} />
								) : isRTL() ? (
									commercialEntityDetails?.guardianUser?.name?.ar?.slice(0, 1)
								) : (
									commercialEntityDetails?.guardianUser?.name?.en?.slice(0, 1)
								)}
							</div>
							<div>
								<div>
									<div className={styles.data_name}>
										<h5>{isRTL() ? commercialEntityDetails?.guardianUser?.name?.ar : commercialEntityDetails?.guardianUser?.name?.en}</h5>

										{commercialEntityDetails?.guardianUser?.is_phone_verified && <Icon name="verified" />}

										<span className={`status ${StatusColors[commercialEntityDetails?.guardianUser?.profile_status!]}`}>
											{t(`COMMON.${commercialEntityDetails?.guardianUser?.profile_status?.toUpperCase()}`)}
										</span>
									</div>

									<div className={styles.data_connection}>
										<Row gutter={[10, 10]}>
											<Col span={12}>
												<Icon name="contact" />
												<span style={{ direction: 'ltr', fontWeight: 500 }}>
													{`+${commercialEntityDetails?.guardianUser?.phone_country_code} ${commercialEntityDetails?.guardianUser?.phone_number}`}
												</span>
											</Col>
											<Col span={12}>
												<Icon name="id" />
												{commercialEntityDetails?.guardianUser?.national_id}
											</Col>
											<Col span={12}>
												<Icon name="location" />
												{commercialEntityDetails?.guardianUser?.country?.name}
											</Col>
											<Col span={12}>
												<Icon name="country2" />
												{commercialEntityDetails?.guardianUser?.address}
											</Col>
											<Col span={12}>
												<Icon name="heart" />
												{commercialEntityDetails?.guardianUser?.date_of_birth
													? dayjs(commercialEntityDetails?.guardianUser?.date_of_birth).format('YYYY-MM-DD')
													: ''}
											</Col>
											{commercialEntityDetails?.guardianUser?.email && (
												<Col span={12}>
													<Icon name="mail" />
													{commercialEntityDetails?.guardianUser?.email}
												</Col>
											)}
										</Row>
									</div>
								</div>
							</div>
						</div>
					</div>

					<Divider />

					<div className="d-flex justify-content-center gap-3 flex-wrap">
						{commercialEntityDetails?.cr_document && (
							<div>
								<PDFViewer link={commercialEntityDetails?.cr_document!} />
								<FilePreview
									className="flex-grow-1 mt-2"
									name={t('COMMERCIAL_ENTITIES.CR_DOCUMENT')}
									downloadLink={commercialEntityDetails?.cr_document}
								/>
							</div>
						)}

						{commercialEntityDetails?.approved_authorization_document && (
							<div>
								<Divider />
								<PDFViewer link={commercialEntityDetails?.approved_authorization_document!} />
								<FilePreview
									className="flex-grow-1 mt-2"
									name={t('COMMERCIAL_ENTITIES.APPROVED_AUTHORIZED_DOCUMENT')}
									downloadLink={commercialEntityDetails?.approved_authorization_document}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showApprovingConfirmation}
				confirmText={t('COMMERCIAL_ENTITIES.APPROVE')}
				confirmType="primary"
				onDiscard={() => {
					setShowApprovingConfirmation(false);
				}}
				title={t('COMMERCIAL_ENTITIES.APPROVE_TITLE')}
				description={t('COMMERCIAL_ENTITIES.APPROVE_DESCRIPTION')}
				onConfirm={onApprove}
				isLoading={isApproving}
			/>

			<ConfirmModalComponent
				open={showUnlinkingConfirmation}
				confirmText={t('COMMERCIAL_ENTITIES.UNLINK')}
				onDiscard={() => {
					setShowUnlinkingConfirmation(false);
				}}
				title={t('COMMERCIAL_ENTITIES.UNLINK_TITLE')}
				description={t('COMMERCIAL_ENTITIES.UNLINK_DESCRIPTION')}
				onConfirm={onUnlink}
				isLoading={isUnlinking}
			/>

			<ConfirmModalComponent
				open={showRejectingConfirmation}
				confirmText={t('COMMERCIAL_ENTITIES.REJECT')}
				onDiscard={() => {
					setShowRejectingConfirmation(false);
				}}
				title={t('COMMERCIAL_ENTITIES.REJECT_TITLE')}
				description={t('COMMERCIAL_ENTITIES.REJECT_DESCRIPTION')}
				onConfirm={onReject}
				isLoading={isRejecting}
				inputMessage
			/>

			<Modal
				open={showEditModal}
				title={t('COMMERCIAL_ENTITIES.UPDATE_TITLE')}
				okText={t('COMMON.UPDATE')}
				okButtonProps={{ size: 'middle', className: 'px-3 py-1', loading: isUpdating }}
				cancelButtonProps={{ size: 'middle', className: 'px-3 py-1' }}
				onCancel={() => {
					setShowEditModal(false);
				}}
				onOk={() => updateForm.submit()}>
				<div className="my-4">
					<Form form={updateForm} layout="vertical" onFinish={onUpdate}>
						<Form.Item
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							label={t('COMMERCIAL_ENTITIES.NAME_AR')}
							name="[name][ar]"
							style={{ maxWidth: 'unset' }}>
							<Input />
						</Form.Item>

						<Form.Item
							rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
							label={t('COMMERCIAL_ENTITIES.NAME_EN')}
							name="[name][en]"
							style={{ maxWidth: 'unset' }}>
							<Input />
						</Form.Item>

						<Form.Item
							rules={[
								{ required: true, message: t('COMMON.FIELD_REQUIRED') },
								{ pattern: /^[0-9]+$/, message: t('COMMON.NUMBERS_ONLY') },
								{ len: 10, message: t('COMMERCIAL_ENTITIES.CR_NUMBER_10') },
							]}
							label={t('COMMERCIAL_ENTITIES.CR_NUMBER')}
							name="cr_number"
							style={{ maxWidth: 'unset' }}>
							<Input />
						</Form.Item>

						<Form.Item
							rules={[
								{ required: true, message: t('COMMON.FIELD_REQUIRED') },
								{ pattern: /^[0-9]+$/, message: t('COMMON.NUMBERS_ONLY') },
								{ len: 10, message: t('COMMERCIAL_ENTITIES.UNIFIED_CR_10') },
							]}
							label={t('COMMERCIAL_ENTITIES.UNIFIED_CR')}
							name="unified_cr"
							style={{ maxWidth: 'unset' }}>
							<Input />
						</Form.Item>
					</Form>
				</div>
			</Modal>
		</>
	);
}

export default CommercialEntityViewPage;
